import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import { setIsLoading } from "../slices/settingMailNotiSlice";
import * as services from "../service/settingMailnotiApi";

const useCreateSettingNotify = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [shiftOptions, setShiftOptions] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const leaveTypeOptions = [
        {
            key: "leaveRequest",
            labelEn: "Leave request",
            labelTh: "คำขอลา"
        }
    ]

    const modifiedLeaveOptions = leaveTypeOptions.map(item => ({
        value: item.key,
        label: i18n.language === 'th' ? item.labelTh : item.labelEn
    }));

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions;
    }, [i18n.language, departmentListOption]);

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions;
    }, [i18n.language, positionOptions]);

    const modifiedBranchOptions = useMemo(() => {
        const modifiedOptions = branchOptions?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions;
    }, [i18n.language, branchOptions]);

    const modifiedShiftOptions = useMemo(() => {
        const modifiedOptions = shiftOptions?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions;
    }, [i18n.language, shiftOptions]);

    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setDepartmentListOption(response.data));
        } catch (error) {
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getPosition(values);
                setPositionOptions(response.data);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    const getBranch = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getBranch(values);
                setBranchOptions(response.data);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    const getShift = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getShiftOptions(values);
                setShiftOptions(response.data);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    useEffect(() => {
        if (currentCompany) {
            getPosition();
            getDepartment();
            getBranch();
            getShift();
        }
    }, [currentCompany, getBranch, getDepartment, getPosition, getShift])

    return {
        modifiedLeaveOptions,
        modifiedDepartmentOption,
        modifiedPositionOptions,
        modifiedBranchOptions,
        modifiedShiftOptions,
        isModalOpen,
        setIsModalOpen,
    }
}

export default useCreateSettingNotify;